import { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link, LinkButton } from '@backstage/core-components';
import { Badge, InputBase, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import NotificationsIcon from '@material-ui/icons/NotificationsNoneOutlined';
import SearchIcon from '@material-ui/icons/Search';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { useByxT } from '../shared/translations';
import { byxBrand } from '../theme/tokens';
import { CONTENT_MAX_WIDTH } from './layout';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1.5, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  // Same centered column as the design, so on wide screens the search and
  // the actions don't drift to the far edges.
  inner: {
    maxWidth: CONTENT_MAX_WIDTH,
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  search: {
    flex: '1 1 320px',
    maxWidth: 640,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: 44,
    padding: theme.spacing(0, 1.5),
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.default,
    // Keyboard focus (U8-6): the 2px primary ring with a 2px offset around
    // the whole box, the only ring (the border stays as it is).
    '&:focus-within': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  searchIcon: {
    flex: 'none',
    color: theme.palette.text.secondary,
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
  },
  shortcut: {
    flex: 'none',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
    lineHeight: '18px',
    padding: '0 7px',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  actions: {
    flex: '1 1 auto',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  iconLink: {
    width: 44,
    height: 44,
    display: 'grid',
    placeItems: 'center',
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    color: theme.palette.text.primary,
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  badge: {
    backgroundColor: byxBrand.amber,
    color: byxBrand.navy,
    fontWeight: 700,
  },
  avatarLink: {
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
  },
}));

export const SEARCH_PATH = '/search';

/**
 * Where a search for `query` goes. From the Busca page itself the page's
 * filters and page size stay, and paging starts again; from anywhere else
 * it is a new search.
 */
export function searchHref(
  query: string,
  location: { pathname: string; search: string },
) {
  const params = new URLSearchParams(
    location.pathname === SEARCH_PATH ? location.search : '',
  );
  params.delete('pageCursor');
  if (query) {
    params.set('query', query);
  } else {
    params.delete('query');
  }
  const search = params.toString();
  return search ? `${SEARCH_PATH}?${search}` : SEARCH_PATH;
}

/** Typing in a field must keep the "/" character instead of jumping to search. */
function isEditable(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  );
}

/**
 * Top bar from the "Backstage BYX" design: catalog-wide search (focused by
 * "/"), shortcut to the scaffolder, notifications and the user's account.
 */
export const TopBar = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useByxT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');

  // On the Busca page the box shows the page's query (AC-12) and stays
  // editable; elsewhere it keeps what was typed.
  const onSearchPage = location.pathname === SEARCH_PATH;
  const urlQuery = onSearchPage
    ? new URLSearchParams(location.search).get('query') ?? ''
    : undefined;
  useEffect(() => {
    if (urlQuery !== undefined) {
      setTerm(urlQuery);
    }
  }, [urlQuery]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== '/' ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        isEditable(event.target)
      ) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    navigate(searchHref(term.trim(), location));
  };

  return (
    <header className={classes.root}>
      <div className={classes.inner}>
        <form role="search" className={classes.search} onSubmit={onSubmit}>
          <SearchIcon fontSize="small" className={classes.searchIcon} />
          <InputBase
            inputRef={inputRef}
            className={classes.input}
            placeholder={t('nav.topBar.searchPlaceholder')}
            inputProps={{ 'aria-label': t('nav.topBar.searchLabel') }}
            value={term}
            onChange={event => setTerm(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                inputRef.current?.blur();
              }
            }}
          />
          <kbd className={classes.shortcut} aria-hidden="true">
            /
          </kbd>
        </form>
        <div className={classes.actions}>
          <LinkButton
            to="/create"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            {t('nav.topBar.create')}
          </LinkButton>
          {/* The plugin's sidebar item, rendered as the bell: it keeps the
              notifications signal subscription, the pop-ups for new
              notifications and the unread counter in the page title. */}
          <NotificationsSidebarItem
            renderItem={({ unreadCount, to, onClick }) => (
              <Link
                to={to}
                onClick={onClick}
                underline="none"
                className={classes.iconLink}
                aria-label={
                  unreadCount > 0
                    ? t('nav.topBar.notificationsUnread', {
                        count: String(unreadCount),
                      })
                    : t('nav.topBar.notifications')
                }
              >
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  overlap="rectangular"
                  classes={{ badge: classes.badge }}
                >
                  <NotificationsIcon />
                </Badge>
              </Link>
            )}
          />
          <Link
            to="/settings"
            underline="none"
            className={classes.avatarLink}
            aria-label={t('nav.topBar.account')}
          >
            <UserSettingsSignInAvatar size={40} />
          </Link>
        </div>
      </div>
    </header>
  );
};
